import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Popover, Tooltip, OverlayTrigger } from 'react-bootstrap';
import {
  Address,
  Phone,
  Name,
  Email,
  getFormattedName,
  getFormattedCityStateZip,
} from 'ecs-react-components/dist/index.es';

import { messages } from './PersonalContactCard.i18n';

class PersonalContactCard extends React.Component {
  getPersonName() {
    return {
      firstName: this.props.contact.contactFirstName,
      lastName: this.props.contact.contactLastName,
      suffix: this.props.contact.suffix,
      title: this.props.contact.relationship };
  }

  getNumberOfLines = () => {
    const {
      contact,
    } = this.props;

    let maxLengthBeforeNameWraps = 39;
    let maxLengthBeforeAddress1Wraps = 41;
    let maxLengthBeforeAddress2Wraps = 41;
    let maxLengthBeforeCityStateZipWraps = 44;
    if (this.props.windowSize === 'large') {
      maxLengthBeforeNameWraps = 39;
      maxLengthBeforeAddress1Wraps = 41;
      maxLengthBeforeAddress2Wraps = 41;
      maxLengthBeforeCityStateZipWraps = 44;
    } else if (this.props.windowSize === 'medium') {
      maxLengthBeforeNameWraps = 29;
      maxLengthBeforeAddress1Wraps = 30;
      maxLengthBeforeCityStateZipWraps = 42;
    } else if (this.props.windowSize === 'small') {
      maxLengthBeforeNameWraps = 36;
      maxLengthBeforeAddress2Wraps = 38;
      maxLengthBeforeCityStateZipWraps = 48;
    } else {
      maxLengthBeforeNameWraps = 39;
      maxLengthBeforeAddress1Wraps = 41;
      maxLengthBeforeAddress2Wraps = 41;
      maxLengthBeforeCityStateZipWraps = 44;
    }

    let counter = 1; // Account for name line. It will always be present.
    if (getFormattedName(this.getPersonName()).length > maxLengthBeforeNameWraps) {
      counter += 1;
    }
    if (contact.address1) {
      counter += 1;
    }
    if (contact.address1 && contact.address1.length > maxLengthBeforeAddress1Wraps) {
      counter += 1;
    }
    if (contact.address2) {
      counter += 1;
    }
    if (contact.address2 && contact.address2.length > maxLengthBeforeAddress2Wraps) {
      counter += 1;
    }

    if (contact.city || contact.state || contact.zipCode) {
      counter += 1;
    }
    const cityStateZipLine = getFormattedCityStateZip(contact.city, contact.state, contact.zipCode);
    if (cityStateZipLine.length > maxLengthBeforeCityStateZipWraps) {
      counter += 1;
    }

    if (contact.contactEmail) {
      counter += 1;
    }
    if (contact.homePhone) {
      counter += 1;
    }
    if (contact.workPhone) {
      counter += 1;
    }
    if (contact.mobilePhone) {
      counter += 1;
    }

    return counter;
  }

  getContactInfoOverlay = () => (
    <Popover title={this.props.intl.formatMessage(messages.contactDetails.popOverTitle)} id="contactPopOver">
      {this.renderContactInfoFull()}
    </Popover>
  );

  getAuthorizeMessage = () => {
    const { contact } = this.props;
    if (contact.authorizedRepresentativeUserId && contact.isAuthorizedRepresentativeActive) {
      return this.props.intl.formatMessage(messages.contactDetails.authorizedMessage);
    }
    if (contact.invitationId && !contact.isInvitationLocked && !contact.isInvitationExpired) {
      return this.props.intl.formatMessage(messages.contactDetails.pendingAuthorizedMessage);
    }

    return this.props.intl.formatMessage(messages.contactDetails.notAuthorizedMessage);
  };

  isExtraSmallView = () => (this.props.windowSize === 'xsmall');

  renderContactInfoFull = () => {
    const { contact } = this.props;
    return (
      <div>
        <div>
          <strong><Name name={this.getPersonName()} otherProperties={{ tabIndex: -1 }} /></strong>
          {contact.isPrimaryContact ? this.renderPrimaryIndicator() : null}
        </div>
        <Address
          address={contact}
          otherProperties={{ tabIndex: -1 }}
        />
        <Phone {...contact}
          otherProperties={{ tabIndex: -1 }}
        />
        <Email email={contact.contactEmail}
          otherProperties={{ tabIndex: -1 }}
        />
      </div>
    );
  }

  renderDescribedByHiddenDetail = (describedById) => (
    <div className="sr-only" id={describedById}>
      {this.renderContactInfoFull()}
    </div>
  );

  renderContactInfoSubset = () => {
    const numberOfLines = this.getNumberOfLines();
    const { contact } = this.props;
    const describedById = 'describedById_' + contact.contactId;
    return (
      <div>
        <div>
          <strong><Name name={this.getPersonName()} otherProperties={{ tabIndex: 0 }} /></strong>
          {contact.isPrimaryContact ? this.renderPrimaryIndicator() : null}
        </div>
        <Address
          address={
            {
              address1: numberOfLines > 12 ? null : contact.address1,
              address2: numberOfLines > 11 ? null : contact.address2,
              city: numberOfLines > 10 ? null : contact.city,
              state: numberOfLines > 10 ? null : contact.state,
              zipCode: numberOfLines > 10 ? null : contact.zipCode,
            }
          }
          otherProperties={{ tabIndex: 0 }}
        />
        <Phone
          homePhone={ numberOfLines > 9 ? null : contact.homePhone }
          mobilePhone={ numberOfLines > 8 ? null : contact.mobilePhone }
          workPhone={ numberOfLines > 7 ? null : contact.workPhone }
          otherProperties={{ tabIndex: 0 }}
        />
        { numberOfLines > 7 ? null : <Email email={contact.contactEmail} otherProperties={{ tabIndex: 0 }} />}
        { numberOfLines > 7 ? this.renderMoreLink(describedById) : null }
        { numberOfLines > 7 ? this.renderDescribedByHiddenDetail(describedById) : null }
      </div>
    );
  }

  renderPrimaryIndicator = () => {
    const tooltipId = 'primary_tooltip' + this.props.contact.contactId;
    const tooltip = (
        <Tooltip id={tooltipId} role="tooltip">
          {this.props.intl.formatMessage(messages.contactDetails.primaryContact)}
        </Tooltip>
    );
    return (
      <OverlayTrigger placement="top" overlay={tooltip}>
        <a tabIndex="0" type="button" data-toggle={tooltipId} data-placement="top" className="ml-1">
          <span className="fa-solid fa-star" aria-hidden="true"></span>
        </a>
      </OverlayTrigger >);
  }

  renderMoreLink = (describedById) => (
    <OverlayTrigger trigger="click" rootClose placement="top" overlay={this.getContactInfoOverlay()}>
      <button tabIndex="0" className="linkbutton" aria-describedby={describedById}
        role="link"
      >
        {this.props.intl.formatMessage(messages.contactDetails.moreLink)}
      </button>
    </OverlayTrigger >
  );

  render = () => {
    const { contact } = this.props;
    const personalContact = getFormattedName(this.getPersonName(), false);
    return (
    <div className="col">
      <div className="card h-100 contact-card notheme">
        <div className="card-body">
          <div className="row">
            <div className="col">
              {this.props.isDisplayAddContact ? (
              <button tabIndex="0" className="btn btn-link float-right"
                onClick={this.props.editClick(contact.contactId)}
                aria-label={personalContact} role="link" aria-describedby="edit_link"
              >
                <span className="fa-regular fa-pen-to-square mr-1" aria-hidden="true"></span>
                <span id="edit_link">
                  <FormattedMessage {...messages.contactDetails.edit} />
                </span>
              </button>)
                : null}
              {
                this.renderContactInfoFull()
              }
            </div>
          </div>
        </div>
        {
          this.props.isDisplayAddContact ? (
          <div className="card-footer">
            <div tabIndex="0" className="authorize_text">{this.getAuthorizeMessage()}&nbsp; </div>
            <div className="authorize_text">
            <a
              tabIndex="0"
              onClick={this.props.authorizeClick(contact.contactId, contact.contactId)}
              className="underline"
              role="link"
            >
              {this.props.intl.formatMessage(messages.contactDetails.changeButton)}
            </a><br />&nbsp;
            </div>
          </div>) :
            null
        }
      </div>
    </div>);
  }
}

PersonalContactCard.propTypes = {
  editClick: PropTypes.func,
  contact: PropTypes.object,
  windowSize: PropTypes.string,
  intl: PropTypes.object,
  authorizeClick: PropTypes.func,
  isDisplayAddContact: PropTypes.bool,
};

export default injectIntl(PersonalContactCard);